import _ from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';

import RegistrationTemplatesGql from '@/extensions/eventManager/store/graphql/queries/RegistrationTemplates.gql';
import RegistrationTemplateGql from '@/extensions/eventManager/store/graphql/queries/RegistrationTemplate.gql';
import RegistrationTemplatesPaginatedGql from '@/extensions/eventManager/store/graphql/queries/RegistrationTemplatesPaginated.gql';
import CreateRegistrationTemplateGql from '@/extensions/eventManager/store/graphql/mutations/CreateRegistrationTemplate.gql';
import UpdateRegistrationTemplateGql from '@/extensions/eventManager/store/graphql/mutations/UpdateRegistrationTemplate.gql';
import DeleteRegistrationTemplateGql from '@/extensions/eventManager/store/graphql/mutations/DeleteRegistrationTemplate.gql';

// EX_EVNT_RegistrationTemplateInput takes exactly: id, name, description, cms, fields[].
// The loaded template carries more (events_count, __typename, ...) which the input type
// rejects, so the payload is rebuilt from the fields the mutation understands.
const toTemplateInput = template => {
    const fields = (template.fields || []).map(field => {
        const input = _.pick(field, ['id', 'key', 'type', 'label', 'placeholder', 'help_text', 'required', 'width', 'position', 'deleted']);
        input.options = (field.options || []).map(option => _.pick(option, ['id', 'label', 'value', 'position', 'deleted']));
        return input;
    });

    const input = _.pick(template, ['id', 'name', 'description']);
    if (template.cms) input.cms = { id: template.cms.id };
    input.fields = fields;

    return input;
};

export default {
    fetchList: (cmsID = null) =>
        new Promise((resolve, reject) => {
            let variables = {};
            if (cmsID) variables.cms = parseInt(cmsID);

            apolloClient
                .query({
                    query: RegistrationTemplatesGql,
                    variables
                })
                .then(({ data }) => resolve(data.EX_EVNT_RegistrationTemplates))
                .catch(err => reject(err));
        }),
    fetchRegistrationTemplatesPaginated: data =>
        new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            apolloClient
                .query({
                    query: RegistrationTemplatesPaginatedGql,
                    variables: {
                        currentPage: _.get(data, 'pageInfo.currentPage', 1),
                        perPage: _.get(data, 'pageInfo.perPage', 25),
                        order: _.get(data, 'variables.order', 'updated_at'),
                        direction: _.get(data, 'variables.direction', 'desc'),
                        link: _.get(data, 'link.route', '/event-manager/registration-template/'),
                        filters
                    }
                })
                .then(({ data }) => resolve(data.EX_EVNT_RegistrationTemplatesPaginated))
                .catch(err => reject(err));
        }),
    fetchRegistrationTemplate: id => {
        if (id === undefined) throw new Error('required argument is missing');

        let variables = { id: parseInt(id) };

        return new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: RegistrationTemplateGql,
                    variables
                })
                .then(({ data }) => resolve(data.EX_EVNT_RegistrationTemplate))
                .catch(err => reject(err));
        });
    },
    create: template => {
        if (template === undefined) throw new Error('required argument is missing');

        template = toTemplateInput(_.removeDeepByKey(template, '__typename'));
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateRegistrationTemplateGql,
                    variables: { template }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_EVNT_CreateRegistrationTemplate);
                })
                .catch(err => reject(err));
        });
    },
    update: template => {
        if (template === undefined) throw new Error('required argument is missing');

        template = toTemplateInput(_.removeDeepByKey(template, '__typename'));
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdateRegistrationTemplateGql,
                    variables: { template }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_EVNT_UpdateRegistrationTemplate);
                })
                .catch(err => reject(err));
        });
    },
    delete: id => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DeleteRegistrationTemplateGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_EVNT_DeleteRegistrationTemplate);
                })
                .catch(err => reject(err));
        });
    }
};
